import React, { useState, useEffect } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Typography, Box, Paper, Grid, Switch, FormControlLabel,
  List, ListItem, ListItemText, Button, Divider, Alert, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { adminListUsers, adminGetUserNatal, adminUpdatePermissions } from '../services/transitCalendarService';
import { resolveBirthMoment } from '../utils/natalDate';

const AdminUserPanel = () => {
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user is admin when panel expands
    if (expanded) {
      checkAdminAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const checkAdminAccess = async () => {
    const token = localStorage.getItem('transit-calendar-token');
    if (!token) {
      setError('You must be logged in to access admin functions');
      setIsAdmin(false);
      return;
    }

    // Try to load users - if it fails, user is not admin
    await loadUsers();
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('transit-calendar-token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await adminListUsers(token);
      setUsers(result.users || []);
      setIsAdmin(true);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users. You may not have admin access.');
      setIsAdmin(false);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewNatalData = async (user) => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('transit-calendar-token');
      const result = await adminGetUserNatal(token, user.user_id);
      setSelectedUser({ ...user, natal_data: result.userData });
    } catch (err) {
      console.error('Error loading natal data:', err);
      setError('Failed to load natal data for this user');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = async (userId, permissionType) => {
    const user = users.find(u => u.user_id === userId);
    if (!user) return;

    console.log('[AdminUserPanel] Toggling permission:', { userId, permissionType, currentPermissions: user.permissions });

    let updatedPermissions;

    // Handle nested permissions (e.g., 'year_2026.tth_monthly')
    if (permissionType.includes('.')) {
      const [parent, child] = permissionType.split('.');
      updatedPermissions = {
        ...user.permissions,
        [parent]: {
          ...(user.permissions?.[parent] || {}),
          [child]: !(user.permissions?.[parent]?.[child] || false)
        }
      };
    } else {
      // Handle flat permissions (backward compatibility)
      updatedPermissions = {
        ...user.permissions,
        [permissionType]: !user.permissions[permissionType]
      };
    }

    console.log('[AdminUserPanel] Updated permissions:', updatedPermissions);

    // Optimistically update UI
    setUsers(users.map(u =>
      u.user_id === userId ? { ...u, permissions: updatedPermissions } : u
    ));

    try {
      const token = localStorage.getItem('transit-calendar-token');
      const result = await adminUpdatePermissions(token, userId, updatedPermissions);
      console.log('[AdminUserPanel] Permission update successful:', result);
    } catch (err) {
      console.error('[AdminUserPanel] Error updating permissions:', err);
      setError('Failed to update permissions. Changes may not be saved.');

      // Revert on error
      setUsers(users.map(u =>
        u.user_id === userId ? { ...u, permissions: user.permissions } : u
      ));
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mb: 3,
        border: '2px solid',
        borderColor: 'warning.main',
        '&:before': { display: 'none' }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: 'warning.light' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Admin: User Management
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage registered users, view their natal charts, and control feature access.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && isAdmin && (
          <>
            {/* User List */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Registered Users ({users.length})
              </Typography>

              {users.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No users registered yet
                </Typography>
              ) : (
            <List>
              {users.map((user, index) => (
                <React.Fragment key={user.user_id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {user.name}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Registered: {new Date(user.created_at).toLocaleDateString()}
                          </Typography>
                        </>
                      }
                    />

                    <Box sx={{ mt: 2, width: '100%' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleViewNatalData(user)}
                        sx={{ mb: 2 }}
                      >
                        View Natal Chart
                      </Button>

                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                        Cosmic Personal Events Calendar (TTH)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.tth_monthly || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.tth_monthly')}
                                color="primary"
                              />
                            }
                            label="Monthly View"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.tth_annual || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.tth_annual')}
                                color="primary"
                              />
                            }
                            label="Annual View"
                          />
                        </Grid>
                      </Grid>

                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                        Personal Cosmic Activation (NTT)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.ntt_daily !== false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.ntt_daily')}
                                color="success"
                              />
                            }
                            label="Daily (Free)"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.ntt_weekly || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.ntt_weekly')}
                                color="primary"
                              />
                            }
                            label="Weekly"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.ntt_monthly || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.ntt_monthly')}
                                color="primary"
                              />
                            }
                            label="Monthly"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.ntt_annual || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.ntt_annual')}
                                color="primary"
                              />
                            }
                            label="Annual"
                          />
                        </Grid>
                      </Grid>

                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                        Natal to Transit Calendar (NT)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.nt_weekly || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.nt_weekly')}
                                color="primary"
                              />
                            }
                            label="Weekly"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.nt_monthly || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.nt_monthly')}
                                color="primary"
                              />
                            }
                            label="Monthly"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.nt_annual || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.nt_annual')}
                                color="primary"
                              />
                            }
                            label="Annual"
                          />
                        </Grid>
                      </Grid>

                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                        Transit-to-Natal Calendar (TN) - Coming Soon
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.tn_monthly || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.tn_monthly')}
                                color="primary"
                                disabled
                              />
                            }
                            label="Monthly (Not Yet Available)"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.tn_annual || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.tn_annual')}
                                color="primary"
                                disabled
                              />
                            }
                            label="Annual (Not Yet Available)"
                          />
                        </Grid>
                      </Grid>

                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                        Transit-to-Transit Calendar (TT) - No Houses
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.tt_monthly || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.tt_monthly')}
                                color="primary"
                              />
                            }
                            label="Monthly View"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.permissions?.year_2026?.tt_annual || false}
                                onChange={() => handlePermissionToggle(user.user_id, 'year_2026.tt_annual')}
                                color="primary"
                              />
                            }
                            label="Annual View"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>

        {/* Selected User's Natal Data */}
        {selectedUser && (
          <Paper sx={{ p: 3, bgcolor: 'primary.50' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Natal Chart: {selectedUser.name}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Birth Information
                </Typography>
                <Typography variant="body2">
                  <strong>Date & Time:</strong> {(resolveBirthMoment(selectedUser.natal_data)?.localDate || new Date(selectedUser.natal_data.birth_date)).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Location:</strong> {selectedUser.natal_data.birth_location}
                </Typography>
                <Typography variant="body2">
                  <strong>Coordinates:</strong> {selectedUser.natal_data.birth_lat.toFixed(4)}°, {selectedUser.natal_data.birth_lon.toFixed(4)}°
                </Typography>
                <Typography variant="body2">
                  <strong>Timezone:</strong> {selectedUser.natal_data.timezone} (UTC{selectedUser.natal_data.timezone_offset >= 0 ? '+' : ''}{selectedUser.natal_data.timezone_offset})
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Ascendant:</strong> {selectedUser.natal_data.ascendant_sign} {Math.floor(selectedUser.natal_data.ascendant_longitude % 30)}°
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Natal Planet Positions
                </Typography>
                <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                  {selectedUser.natal_data.natal_positions && Object.entries(selectedUser.natal_data.natal_positions).map(([planet, data]) => (
                    <Typography key={planet} variant="body2" sx={{ mb: 0.5 }}>
                      <strong>{planet}:</strong> {data.sign} {Math.floor(data.degree)}°{Math.round((data.degree % 1) * 60)}'
                      {data.isRetrograde && ' ℞'}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>

            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedUser(null)}
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          </Paper>
        )}

            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Admin Panel:</strong> Connected to AWS DynamoDB. User data and permissions are managed via secure admin-only API endpoints with JWT authentication.
              </Typography>
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AdminUserPanel;
